import { LinkButton } from "~/common/components/Link";
import { Card, Stack, styled, Typography } from "~/common/mui";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import {
  C_OUTLINE_VARIANT,
  C_SURFACE_CONTAINER_HIGH,
  C_SURFACE_CONTAINER_HIGHEST,
} from "~/common/components/icon/Colors";
import { useTranslation } from "react-i18next";

interface UploadSectionProps {
  disabled: boolean;
  isDragging: boolean;
  selectFile: () => void;
  fileFormatText?: string;
  bulkEntry?: boolean;
}

export const FileUploadArea = ({ disabled, selectFile, fileFormatText, isDragging, bulkEntry }: UploadSectionProps) => {
  const { t } = useTranslation();

  return (
    <UploadCard>
      <UploadContent>
        <CloudIcon isDragging={isDragging} bulkEntry={bulkEntry} />
        <Stack gap={1}>
          {isDragging && bulkEntry ? (
            <Typography variant="labelLarge" color="primary.onContainer">
              {t("bulkEntry.drop.text")}
            </Typography>
          ) : (
            <Typography variant="labelLarge">
              <LinkButton disabled={disabled} onClick={selectFile}>
                {t("quoteSplit.uploadDialog.selectFile")}
              </LinkButton>
              &nbsp;
              {t("quoteSplit.uploadDialog.selectFileOr")}
            </Typography>
          )}
          {(!isDragging || !bulkEntry) && <Typography variant="bodySmall">{fileFormatText}</Typography>}
        </Stack>
      </UploadContent>
    </UploadCard>
  );
};

const UploadCard = styled(Card)({
  display: "flex",
  height: "136px",
  padding: "32px 0",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: "16px",
  alignSelf: "stretch",
  backgroundColor: C_SURFACE_CONTAINER_HIGH,
  border: `1px dashed ${C_OUTLINE_VARIANT}`,
  borderRadius: "16px",
  "&:hover": {
    backgroundColor: C_SURFACE_CONTAINER_HIGHEST,
  },
});

const UploadContent = styled(Stack)({
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  gap: "24px",
});

const CloudIcon = styled(CloudUploadOutlinedIcon, {
  shouldForwardProp: (prop) => prop !== "isDragging" && prop !== "bulkEntry",
})<{ isDragging?: boolean; bulkEntry?: boolean }>(({ theme, isDragging, bulkEntry }) => ({
  width: "72px",
  height: "72px",
  color: isDragging && bulkEntry ? theme.palette.primary.onContainer : theme.palette.text.label,
}));
